import { ReactElement, useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { Grid, Typography, Container, Button } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import ReactMarkdown, { ExtraProps } from 'react-markdown';
import { useTranslation } from 'react-i18next';
import { getDateFormattedInboxMessage } from 'shared/dates';
import { useAppInfo } from 'hooks/useAppInfo';
import { ANALYTICS_EVENTS, logEvent } from 'shared/analytics';
import { usePostHog } from 'posthog-js/react';
import { InboxMessage } from '../../inboxMessages.types';
import { ActionButton } from './ActionButton';

const useStyles = makeStyles()((theme) => ({
  root: {
    width: '100%',
    maxHeight: '100%',
    overflow: 'auto',
    position: 'relative',
    overscrollBehavior: 'none',
  },
  contentContainer: {
    paddingTop: '0px !important',
    paddingBottom: '0px !important',
  },
  content: {
    fontSize: theme.typography.body1.fontSize,
    '& a': {
      color: 'rgba(255, 87, 34, 1)',
    },
    '& p': {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    maxWidth: '100%',
  },
  stickyActions: {
    position: 'sticky',
    left: 0,
    bottom: 0,
    paddingTop: 24,
    paddingBottom: 24,
    backgroundColor: theme.palette.background.paper,
    // Match the parent Dialog's Paper elevation overlay so the sticky footer
    // blends seamlessly with the rest of the surface in dark mode.
    backgroundImage: 'var(--Paper-overlay)',
  },
  shadow: {
    boxShadow: theme.shadows[10],
  },
}));

type InboxMessageContentProps = {
  message: InboxMessage;
  onClose?: () => void;
};

const RenderMarkdownImage = ({
  node,
  ...imgProps
}: React.ComponentProps<'img'> & ExtraProps): ReactElement => (
  // eslint-disable-next-line jsx-a11y/alt-text
  <img {...imgProps} style={{ maxWidth: '100%', margin: '16px 0px' }} />
);

const customMarkdownComponents = {
  img: RenderMarkdownImage,
};

export const InboxMessageContent = (
  props: InboxMessageContentProps,
): JSX.Element => {
  const { message, onClose } = props;
  const { classes, cx } = useStyles();
  const { appVersion } = useAppInfo();
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const posthog = usePostHog();

  useEffect(() => {
    logEvent({
      name: ANALYTICS_EVENTS.VIEWED.INBOX_MESSAGE,
      params: { campaign: message.campaign, subcampaign: message.subCampaign },
    });
    logEvent({
      name: `${ANALYTICS_EVENTS.VIEWED.INBOX_MESSAGE}_${message.campaign}`,
      params: {},
    });

    posthog.capture('read_inbox_message', {
      campaign: message.campaign,
      subcampaign: message.subCampaign,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const containerElement = containerRef.current;

  const isScrollable = containerElement
    ? containerElement.scrollHeight >
      containerElement.getBoundingClientRect().height
    : false;

  const requiresUpgrade = Boolean(
    Capacitor.isNativePlatform() &&
      message.minAppVersion &&
      appVersion < message.minAppVersion,
  );

  const renderActions = (): JSX.Element => {
    return (
      <>
        {message.ctaText && message.ctaRoute && (
          <>
            {requiresUpgrade && (
              <Grid size={12}>
                <Typography>
                  <b>{t('messageDetail.requiresUpdate')}</b>
                </Typography>
              </Grid>
            )}

            <Grid size={12}>
              <ActionButton
                text={message.ctaText}
                route={message.ctaRoute}
                requiresUpgrade={requiresUpgrade}
                popup={message.mapPopupOnly}
                onClose={onClose}
              />
            </Grid>
          </>
        )}

        {message.mapPopupOnly && onClose && (
          <Grid size={12}>
            <Button fullWidth size="large" onClick={onClose} color="neutral">
              {t('common.close')}
            </Button>
          </Grid>
        )}
      </>
    );
  };

  return (
    <div
      className={classes.root}
      ref={containerRef}
      data-body-scroll-lock-ignore
      data-testid="inbox-message-detail"
    >
      <Container maxWidth="lg" sx={{ width: '100%', py: 3 }}>
        <Grid container spacing={2} sx={{ width: '100%' }}>
          <Grid size={12}>
            <Typography variant="h2">{message.title}</Typography>
            {!message.mapPopupOnly && (
              <Typography color="textSecondary">
                {getDateFormattedInboxMessage(message.dateCreated)}
              </Typography>
            )}
          </Grid>

          <Grid
            size={12}
            className={classes.contentContainer}
            data-testid="inbox-message-body"
          >
            <ReactMarkdown
              className={classes.content}
              components={customMarkdownComponents}
            >
              {message.messageMarkdown}
            </ReactMarkdown>
          </Grid>

          {!message.mapPopupOnly && renderActions()}
        </Grid>
      </Container>

      {message.mapPopupOnly && (
        <div
          className={cx(classes.stickyActions, isScrollable && classes.shadow)}
        >
          <Container maxWidth={false}>
            <Grid container spacing={2} sx={{ width: '100%' }}>
              {renderActions()}
            </Grid>
          </Container>
        </div>
      )}
    </div>
  );
};
